import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import { playNotificationSound, showBrowserNotification, requestNotificationPermission } from "@/services/chatNotification";
import {
  ClientChatMessage,
  getClientChatInfo,
  markClientChatRead,
  getClientChatMessages,
  sendClientChatMessage,
} from "@/actions/client-portal";
import { socketService } from "@/services/socketService";

const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  }
  return `${d.getDate()} ${monthShort[d.getMonth()]} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

const ClientChatWidget = () => {
  const [open, setOpen] = useState(false);
  const openRef = useRef(false); // stable ref so socket handlers don't capture stale `open`
  const [messages, setMessages] = useState<ClientChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [slateId, setSlateId] = useState<number | null>(null);
  const slateIdRef = useRef<number | null>(null); // stable ref for socket handlers
  const [unreadCount, setUnreadCount] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMsgTimeRef = useRef<string | null>(null); // for polling

  const load = useCallback(async (p: number, prepend = false) => {
    if (p === 1) setLoading(true); else setLoadingMore(true);
    const result = await getClientChatMessages(p);
    setTotalPages(result.pages);
    if (result.slate_id) setSlateId(result.slate_id);
    if (prepend) {
      setMessages((prev) => [...result.messages, ...prev]);
    } else {
      setMessages(result.messages);
    }
    if (p === 1) setLoading(false); else setLoadingMore(false);
  }, []);

  // Keep refs in sync with state
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { slateIdRef.current = slateId; }, [slateId]);
  useEffect(() => {
    if (messages.length > 0) lastMsgTimeRef.current = messages[messages.length - 1].created_at;
  }, [messages]);

  // On mount: request notification permission + fetch slate_id for background socket join
  useEffect(() => {
    requestNotificationPermission();
    getClientChatInfo().then((info) => {
      if (info?.slate_id) setSlateId(info.slate_id);
      // Messages our team sent while the client was away
      if (info?.unread_count && !openRef.current) setUnreadCount(info.unread_count);
    });
  }, []);

  // Load messages when widget opens
  useEffect(() => {
    if (!open) return;
    setUnreadCount(0);
    load(1);
  }, [open, load]);

  // Socket + polling — runs once per slateId
  useEffect(() => {
    if (!slateId) return;

    const joinRoom = () => socketService.emit("join_chat_room", { slateId });
    joinRoom();
    socketService.on("connect", joinRoom);

    const seenIds = new Set<number>(); // messages already counted/merged by socket or poll

    const mergeNew = (newMsgs: ClientChatMessage[], fromAdmin = false) => {
      // The same message can arrive by socket and by poll — only count it once
      const fresh = newMsgs.filter((m) => !seenIds.has(m.id));
      fresh.forEach((m) => seenIds.add(m.id));
      if (fresh.length === 0) return;
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        const unseen = fresh.filter((m) => !ids.has(m.id));
        return unseen.length > 0 ? [...prev, ...unseen] : prev;
      });
      const freshAdmin = fresh.filter((m) => m.sender_type !== "client");
      if (!fromAdmin || freshAdmin.length === 0) return;
      if (openRef.current) {
        // Client is looking at the chat — record it as read so badges and WhatsApp alerts agree
        markClientChatRead();
        return;
      }
      setUnreadCount((c) => c + freshAdmin.length);
      const first = freshAdmin[0];
      const preview = first?.message
        ? first.message.slice(0, 60) + (first.message.length > 60 ? "…" : "")
        : "New message";
      playNotificationSound();
      showBrowserNotification("YellowSlate Support", preview);
      toast.info(
        <div className="flex items-start gap-2">
          <MessageCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">YellowSlate Support</div>
            <div className="text-xs text-gray-600 mt-0.5">{preview}</div>
          </div>
        </div>,
        { autoClose: 6000 }
      );
    };

    // Socket: new message in room
    const handleNew = ({ message }: { slateId: number; message: ClientChatMessage }) => {
      if (message.sender_type === "client") return; // own message, already added in send()
      mergeNew([message], true);
    };
    socketService.on("new_chat_message", handleNew);

    // Polling fallback every 15s
    const pollTimer = setInterval(async () => {
      if (!lastMsgTimeRef.current) return;
      const result = await getClientChatMessages(1, lastMsgTimeRef.current);
      if (result.messages.length > 0) {
        const adminMsgs = result.messages.filter((m) => m.sender_type !== "client");
        mergeNew(result.messages, adminMsgs.length > 0);
      }
    }, 15_000);

    return () => {
      socketService.off("connect", joinRoom);
      socketService.emit("leave_chat_room", { slateId });
      socketService.off("new_chat_message", handleNew);
      clearInterval(pollTimer);
    };
  }, [slateId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (open && !loading) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    setText("");
    inputRef.current?.focus();

    const result = await sendClientChatMessage(msg);
    if (result.success && result.message) {
      const sent = result.message;
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
    }
    setSending(false);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-brand text-white shadow-lg hover:bg-brand-hover transition-all hover:scale-105 active:scale-95 flex items-center justify-center"
        style={{ width: 52, height: 52 }}
        title="Chat with us"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full border-2 border-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Chat popup */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          style={{ height: 460 }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-brand text-white">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle size={16} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">YellowSlate Support</div>
              <div className="text-[11px] text-white/70">We reply within 1 business day</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1 bg-gray-50">
            {page < totalPages && (
              <div className="flex justify-center py-1">
                <button
                  onClick={async () => { const p = page + 1; setPage(p); await load(p, true); }}
                  disabled={loadingMore}
                  className="flex items-center gap-1 text-[11px] text-brand disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 size={11} className="animate-spin" /> : <ChevronUp size={11} />}
                  Load earlier
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 size={22} className="animate-spin text-brand" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-10 gap-2 text-gray-400">
                <MessageCircle size={28} className="opacity-40" />
                <p className="text-xs text-center">
                  No messages yet.<br />Send us a message to get started!
                </p>
              </div>
            ) : (
              messages.map((msg) => {
                const isClient = msg.sender_type === "client";
                return (
                  <div key={msg.id} className={`flex ${isClient ? "justify-end" : "justify-start"} mb-1`}>
                    <div className={`max-w-[80%] flex flex-col ${isClient ? "items-end" : "items-start"}`}>
                      {!isClient && (
                        <span className="text-[10px] text-gray-400 mb-0.5 px-1">{msg.sender_name}</span>
                      )}
                      <div
                        className={`px-3 py-1.5 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap break-words ${
                          isClient
                            ? "bg-brand text-white rounded-tr-sm"
                            : "bg-white text-gray-800 border border-gray-200 rounded-tl-sm"
                        }`}
                      >
                        {msg.message}
                      </div>
                      <span className="text-[10px] text-gray-400 mt-0.5 px-1">
                        {formatTime(msg.created_at)}
                        {isClient && <span className="ml-1">{msg.is_read_admin ? "✓✓" : "✓"}</span>}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 bg-white px-3 py-2">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              />
              <button
                onClick={send}
                disabled={!text.trim() || sending}
                className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center hover:bg-brand-hover disabled:opacity-40 transition-colors flex-shrink-0"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientChatWidget;
